import { Fragment, useCallback, useState, type JSX, type ReactNode } from 'react';
import type { AskTheWireEvidence, SitrepDraftResult } from '@tsr/shared-types';
import { authedFetch } from '../api';
import { getSessionId } from '../lib/operatorActions';

/**
 * PublicInfoDraft (#300 — news-LLM council Concept D; PRD §4.19 Public-Information,
 * §4.44 on-demand inference). The "share"-phase action inside the SITL surface
 * (IncidentManagementSection): on demand, draft the incident's Public-Information
 * paragraph GROUNDED ONLY in the wire (cite-or-abstain), as an EDITABLE, CITED
 * candidate the operator edits, owns, and folds into the real report — or discards.
 *
 * OPERATOR IS THE AUTHOR OF RECORD. By construction this surface:
 *   • writes NOTHING on its own — it only POSTs a draft request and renders the
 *     returned candidate (the server writes no sitrep_reports row);
 *   • carries NO forward / insert / promote / publish affordance — only an EDIT
 *     box + Copy, so the draft can never auto-flow into a SITREP or to PEOC;
 *   • always shows the honesty + provenance chrome ("AI-generated draft · not
 *     confirmed") and degrades (abstain / cap / cooldown / failure), never blocks.
 */

const SOURCE_CHIP: Record<string, { label: string; cls: string }> = {
  news: { label: 'news', cls: 'bg-sky-900/60 text-sky-200' },
  social: { label: 'social', cls: 'bg-violet-900/60 text-violet-200' },
  official: { label: 'official', cls: 'bg-emerald-900/60 text-emerald-200' },
  scanner: { label: 'scanner', cls: 'bg-amber-900/60 text-amber-200' },
  brief: { label: 'brief', cls: 'bg-indigo-900/60 text-indigo-200' },
  anomaly: { label: 'spike', cls: 'bg-rose-900/60 text-rose-200' },
};

function chipFor(e: AskTheWireEvidence): { label: string; cls: string } {
  return SOURCE_CHIP[e.sourceType ?? e.kind] ?? { label: e.kind, cls: 'bg-slate-800 text-slate-300' };
}

function relTime(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const m = Math.round(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/** Render the cited-marker preview ([n] → small badges) above the editable box. */
function renderPreview(text: string): ReactNode[] {
  return text.split(/(\[\d+\])/g).map((p, i) => {
    const m = /^\[(\d+)\]$/.exec(p);
    if (m) {
      return (
        <sup key={i} className="mx-px rounded bg-teal-900/70 px-1 text-[9px] font-semibold text-teal-200">
          {m[1]}
        </sup>
      );
    }
    return <Fragment key={i}>{p}</Fragment>;
  });
}

function EvidenceList({
  evidence,
  cited,
}: {
  evidence: readonly AskTheWireEvidence[];
  cited: ReadonlySet<string>;
}): JSX.Element {
  return (
    <div className="mt-1.5 border-t border-teal-800/40 pt-1.5">
      <p className="mb-1 text-[9px] font-semibold uppercase tracking-wider text-slate-500">Evidence (click to verify)</p>
      <ul data-testid="pid-evidence" className="space-y-1">
        {evidence.map((e, i) => {
          const chip = chipFor(e);
          const isCited = cited.has(e.id);
          return (
            <li
              key={e.id}
              data-testid="pid-evidence-item"
              data-cited={isCited ? 'true' : 'false'}
              className={`flex items-baseline gap-1.5 text-[11px] ${isCited ? '' : 'opacity-80'}`}
            >
              <span className="shrink-0 text-[9px] font-semibold text-teal-300/80">[{i + 1}]</span>
              <span className={`shrink-0 rounded px-1 py-px text-[8px] font-semibold uppercase ${chip.cls}`}>
                {chip.label}
              </span>
              {e.origin && (
                <span className="shrink-0 rounded bg-slate-800 px-1 py-px text-[9px] uppercase text-slate-400">
                  {e.origin}
                </span>
              )}
              {e.url ? (
                <a
                  href={e.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="min-w-0 truncate text-sky-400 hover:text-sky-300"
                >
                  {e.title ?? e.url}
                </a>
              ) : (
                <span className="min-w-0 truncate text-slate-300">{e.title ?? '(untitled)'}</span>
              )}
              {e.geoName && <span className="shrink-0 text-[10px] text-slate-500">· {e.geoName}</span>}
              <span className="ml-auto shrink-0 text-[10px] text-slate-500">{relTime(e.observedAt)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function PublicInfoDraft({ incidentId }: { incidentId: string }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SitrepDraftResult | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const run = useCallback(async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const res = await authedFetch('/api/sitreps/draft-public-info', {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ incident_id: incidentId, session_id: getSessionId() }),
      });
      if (res.status === 404) throw new Error('incident not found');
      const data = (await res.json()) as SitrepDraftResult;
      setResult(data);
      setDraft(data.status === 'drafted' ? (data.draft ?? '') : '');
    } catch {
      setError('Draft unavailable — please retry.');
      setResult(null);
    } finally {
      setBusy(false);
    }
  }, [busy, incidentId]);

  const copy = useCallback(async (): Promise<void> => {
    try {
      await navigator.clipboard?.writeText(draft);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }, [draft]);

  const cited = new Set(result?.citationIds ?? []);

  return (
    <div data-testid="public-info-draft" className="space-y-2 border-t border-slate-700/70 pt-3">
      <div className="flex items-center gap-1.5">
        <span className="shrink-0 rounded bg-teal-900/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-teal-200">
          ✎ Public Information Draft
        </span>
        <span className="text-[10px] text-slate-500">grounded in the wire · you own the wording</span>
      </div>

      <button
        type="button"
        data-testid="pid-trigger"
        onClick={() => void run()}
        disabled={busy}
        className="w-full rounded border border-teal-500/60 py-1.5 text-[11px] text-teal-300 hover:bg-teal-600/20 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy ? 'Drafting…' : result ? 'Re-draft from latest signals' : 'Draft Public Information from signals'}
      </button>

      {error && (
        <p data-testid="pid-error" className="text-[11px] text-slate-400">
          {error}
        </p>
      )}

      {result && !error && result.status === 'drafted' && (
        <div data-testid="pid-drafted" className="space-y-1.5">
          {/* Cited-marker preview of what the model proposed (read-only reference). */}
          <p className="text-[11px] italic leading-snug text-slate-400">{renderPreview(result.draft ?? '')}</p>
          {/* The EDITABLE box — the operator owns and rewords this before using it. */}
          <textarea
            data-testid="pid-textarea"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setCopied(false);
            }}
            rows={5}
            className="w-full rounded border border-slate-700 bg-slate-900/70 px-2 py-1 text-[12px] leading-snug text-slate-100 focus:border-teal-600 focus:outline-none"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="pid-copy"
              onClick={() => void copy()}
              className="rounded bg-teal-700 px-2.5 py-1 text-[11px] font-medium text-teal-50 hover:bg-teal-600"
            >
              {copied ? 'Copied ✓' : 'Copy'}
            </button>
            <span data-testid="pid-owner-note" className="text-[10px] text-slate-500">
              Not part of any SITREP until you add it — you are the author of record.
            </span>
          </div>
          <EvidenceList evidence={result.evidence} cited={cited} />
        </div>
      )}

      {result && !error && result.status === 'abstained' && (
        <div data-testid="pid-abstain">
          <p className="text-[12px] font-medium text-slate-300">{result.notConfirmedLine}</p>
          {result.evidence.length > 0 && <EvidenceList evidence={result.evidence} cited={cited} />}
        </div>
      )}

      {result && !error && result.status === 'cooled' && (
        <p data-testid="pid-cooled" className="text-[12px] font-medium text-amber-300/90">
          {result.notConfirmedLine}
        </p>
      )}

      {result && !error && result.status === 'degraded' && (
        <div data-testid="pid-degrade">
          <p className="text-[12px] font-medium text-amber-300/90">{result.notConfirmedLine}</p>
          {(result.degradeReason === 'operator_cap' || result.degradeReason === 'global_cap') && (
            <p className="mt-0.5 text-[10px] text-slate-400">Daily draft limit reached — draft manually from the wire.</p>
          )}
          {result.evidence.length > 0 && <EvidenceList evidence={result.evidence} cited={cited} />}
        </div>
      )}

      {result && !error && (
        <div data-testid="pid-chrome" className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-slate-500">
          <span className="italic text-teal-300/70">{result.provenanceLine} · read-only</span>
          <span className="ml-auto" title="Per-operator daily draft budget (cost cage #301)">
            {result.budget.operatorRemaining} drafts left today
          </span>
        </div>
      )}
    </div>
  );
}
